import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Package, Link2, FileText, Video, Type, Download, Code, Pencil, Trash2,
  GripVertical, ExternalLink, Star, Eye, EyeOff, ImagePlus, Boxes, AlertTriangle,
  FolderOpen,
} from "lucide-react";
import {
  Button, Input, Textarea, Select, Label, FieldHint, Drawer, Modal, ModalFooter,
  Badge, EmptyState, Skeleton, useToast, useConfirm,
} from "@/components/ui";
import { ApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { uploadFile, makeUploadFilename } from "@/lib/upload";
import {
  storeApi, PRODUCT_TYPE_LABELS, FULFILLMENT_LABELS, centsToStr, strToCents,
  type StoreProduct, type StoreResource, type StoreCollection, type StoreVariant,
} from "./api";

const RESOURCE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  link: Link2, file: FileText, video: Video, text: Type, download: Download, embed: Code,
};

export function StoreProductsPage() {
  const toast = useToast();
  const [products, setProducts] = useState<StoreProduct[] | null>(null);
  const [collections, setCollections] = useState<StoreCollection[]>([]);
  const [editing, setEditing] = useState<StoreProduct | "new" | null>(null);
  const [query, setQuery] = useState("");

  const load = useCallback(() => {
    storeApi.products()
      .then((r) => setProducts(r.products))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed to load products"));
    storeApi.collections().then((r) => setCollections(r.collections)).catch(() => setCollections([]));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!products) return null;
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      (p.sku ?? "").toLowerCase().includes(q) ||
      (p.description ?? "").toLowerCase().includes(q));
  }, [products, query]);

  const inventoryLabel = (p: StoreProduct) => {
    if (!p.track_inventory) return "—";
    const qty = p.has_variants
      ? (p.variants ?? []).reduce((s, v) => s + v.inventory_quantity, 0)
      : p.inventory_quantity;
    return (
      <span className={qty === 0 ? "text-rose-400" : qty <= 5 ? "text-amber-400" : undefined}>
        {qty === 0 ? "Sold out" : `${qty} in stock`}
      </span>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Products</h1>
          <p className="mt-1 text-sm text-zinc-400">Everything you sell — prices, variants, inventory, and digital content.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products…" className="w-48" />
          <Link
            to="/store/collections"
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2 text-sm font-medium text-zinc-300 transition hover:bg-zinc-900 hover:text-zinc-100"
          >
            <FolderOpen className="h-4 w-4" /> Collections
          </Link>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>New product</Button>
        </div>
      </div>

      {filtered === null ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Package}
          title={query ? "No matches" : "No products yet"}
          description={query ? "Try a different search." : "Create your first product to start selling."}
          action={!query ? <Button onClick={() => setEditing("new")}>New product</Button> : undefined}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">Product</th>
                <th className="hidden px-4 py-2.5 font-medium md:table-cell">Type</th>
                <th className="px-4 py-2.5 font-medium">Price</th>
                <th className="hidden px-4 py-2.5 font-medium sm:table-cell">Inventory</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/70">
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="cursor-pointer bg-zinc-950 transition hover:bg-zinc-900/50"
                  onClick={() => setEditing(p)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.image_url ? (
                        <img src={p.image_url} alt="" className="h-9 w-9 shrink-0 rounded-lg border border-zinc-800 object-cover" />
                      ) : (
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-zinc-600">
                          <Package className="h-4 w-4" />
                        </span>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 font-medium text-zinc-100">
                          {p.featured && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />}
                          <span className="truncate">{p.name}</span>
                          {p.has_variants && (
                            <span className="inline-flex items-center gap-1 rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-medium text-zinc-400">
                              <Boxes className="h-3 w-3" /> {p.variants?.length ?? 0}
                            </span>
                          )}
                        </div>
                        {p.subtitle && <div className="truncate text-xs text-zinc-500">{p.subtitle}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="hidden px-4 py-3 text-zinc-400 md:table-cell">
                    {PRODUCT_TYPE_LABELS[p.product_type] ?? p.product_type}
                  </td>
                  <td className="px-4 py-3 text-zinc-300">
                    {p.min_amount_cents !== p.max_amount_cents
                      ? `${formatMoney(p.min_amount_cents, p.currency)}–${formatMoney(p.max_amount_cents, p.currency)}`
                      : formatMoney(p.amount_cents, p.currency)}
                    {p.pricing_type === "subscription" && <span className="text-zinc-500">/{p.recurring_interval}</span>}
                  </td>
                  <td className="hidden px-4 py-3 text-zinc-400 sm:table-cell">{inventoryLabel(p)}</td>
                  <td className="px-4 py-3">
                    {p.published
                      ? <Badge color="emerald" size="sm">Live</Badge>
                      : <Badge color="zinc" size="sm">Draft</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-500">
                    <Pencil className="ml-auto h-4 w-4" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <ProductEditor
          product={editing === "new" ? null : editing}
          collections={collections}
          onClose={() => setEditing(null)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}

// ─── Image field with R2 upload ─────────────────────────────────────────────
function ImageField({ value, onChange, label = "Image" }: {
  value: string;
  onChange: (url: string) => void;
  label?: string;
}) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const pick = async (file: File) => {
    setUploading(true);
    try {
      const r = await uploadFile(file, { path: `store/products/${makeUploadFilename(file.name, "product")}` });
      onChange(r.publicUrl);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt="" className="h-14 w-14 rounded-lg border border-zinc-800 object-cover" />
        ) : (
          <span className="flex h-14 w-14 items-center justify-center rounded-lg border border-dashed border-zinc-700 text-zinc-600">
            <ImagePlus className="h-5 w-5" />
          </span>
        )}
        <div className="flex-1 space-y-1.5">
          <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="https://… or upload →" />
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" loading={uploading} onClick={() => fileRef.current?.click()}>
              Upload image
            </Button>
            {value && <Button size="sm" variant="ghost" onClick={() => onChange("")}>Remove</Button>}
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void pick(f); e.target.value = ""; }}
        />
      </div>
    </div>
  );
}

// ─── Editor drawer ─────────────────────────────────────────────────────────
type EditorTab = "details" | "pricing" | "variants" | "content";

function ProductEditor({
  product, collections, onClose, onSaved,
}: {
  product: StoreProduct | null;
  collections: StoreCollection[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const isNew = product === null;
  const [tab, setTab] = useState<EditorTab>("details");
  const [saving, setSaving] = useState(false);
  const [id, setId] = useState<string | null>(product?.id ?? null);

  const [form, setForm] = useState({
    name: product?.name ?? "",
    subtitle: product?.subtitle ?? "",
    product_type: product?.product_type ?? "physical",
    fulfillment: product?.fulfillment ?? "shipped",
    pricing_type: product?.pricing_type ?? "one_time",
    amount: product ? centsToStr(product.amount_cents) : "0.00",
    compare_at: product?.compare_at_cents ? centsToStr(product.compare_at_cents) : "",
    currency: product?.currency ?? "USD",
    recurring_interval: product?.recurring_interval ?? "month",
    badge: product?.badge ?? "",
    description: product?.description ?? "",
    long_description: product?.long_description ?? "",
    highlights: (product?.highlights ?? []).join("\n"),
    image_url: product?.image_url ?? "",
    payment_link: product?.payment_link ?? "",
    published: product?.published ?? false,
    featured: product?.featured ?? false,
    sort_order: product?.sort_order ?? 0,
    sku: product?.sku ?? "",
    track_inventory: product?.track_inventory ?? false,
    inventory_quantity: product?.inventory_quantity ?? 0,
    allow_backorder: product?.allow_backorder ?? false,
    weight_grams: product?.weight_grams != null ? String(product.weight_grams) : "",
    collection_ids: product?.collection_ids ?? [],
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Please give the product a name."); return; }
    setSaving(true);
    const body: Partial<StoreProduct> = {
      name: form.name.trim(),
      subtitle: form.subtitle || null,
      product_type: form.product_type as StoreProduct["product_type"],
      fulfillment: form.fulfillment as StoreProduct["fulfillment"],
      pricing_type: form.pricing_type as StoreProduct["pricing_type"],
      amount_cents: strToCents(form.amount),
      compare_at_cents: form.compare_at ? strToCents(form.compare_at) : null,
      currency: form.currency || "USD",
      recurring_interval: form.pricing_type === "subscription" ? form.recurring_interval : null,
      badge: form.badge || null,
      description: form.description || null,
      long_description: form.long_description || null,
      highlights: form.highlights.split("\n").map((s) => s.trim()).filter(Boolean),
      image_url: form.image_url || null,
      payment_link: form.payment_link || null,
      published: form.published,
      featured: form.featured,
      sort_order: Number(form.sort_order) || 0,
      sku: form.sku || null,
      track_inventory: form.track_inventory,
      inventory_quantity: Number(form.inventory_quantity) || 0,
      allow_backorder: form.allow_backorder,
      weight_grams: form.weight_grams ? Number(form.weight_grams) : null,
      collection_ids: form.collection_ids,
    };
    try {
      if (isNew && !id) {
        const r = await storeApi.createProduct(body as Partial<StoreProduct> & { name: string; amount_cents: number });
        setId(r.product.id);
        toast.success("Product created");
      } else {
        await storeApi.updateProduct(id!, body);
        toast.success("Saved");
      }
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!id) return;
    if (!(await confirm({ title: "Archive this product?", description: "It will be removed from the store. Existing orders keep their history.", confirmLabel: "Archive", danger: true }))) return;
    try {
      await storeApi.deleteProduct(id);
      toast.success("Archived");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };

  const TABS: { key: EditorTab; label: string; needsId?: boolean }[] = [
    { key: "details", label: "Details" },
    { key: "pricing", label: "Pricing & inventory" },
    { key: "variants", label: "Variants", needsId: true },
    { key: "content", label: "Digital content", needsId: true },
  ];

  return (
    <Drawer open onClose={onClose} title={isNew && !id ? "New product" : form.name || "Edit product"} width="xl">
      <div className="flex h-full flex-col">
        <div className="flex gap-1 overflow-x-auto border-b border-zinc-800 px-5 pt-3">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              disabled={t.needsId && !id}
              className={`whitespace-nowrap rounded-t-md px-3 py-2 text-sm font-medium transition ${
                tab === t.key ? "bg-zinc-900 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"
              } disabled:cursor-not-allowed disabled:opacity-40`}
            >
              {t.label}{t.needsId && !id ? " (save first)" : ""}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === "details" && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="p-name">Name</Label>
                <Input id="p-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Classic Tee" />
              </div>
              <div>
                <Label htmlFor="p-sub">Short tagline</Label>
                <Input id="p-sub" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Heavyweight organic cotton" />
              </div>
              <div>
                <Label htmlFor="p-desc">Card description</Label>
                <Textarea id="p-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} />
              </div>
              <div>
                <Label htmlFor="p-long">Full description</Label>
                <Textarea id="p-long" rows={4} value={form.long_description} onChange={(e) => set("long_description", e.target.value)} />
              </div>
              <div>
                <Label htmlFor="p-hl">Highlights (one per line)</Label>
                <Textarea id="p-hl" rows={3} value={form.highlights} onChange={(e) => set("highlights", e.target.value)} placeholder={"220gsm organic cotton\nDouble-stitched seams"} />
              </div>

              <ImageField value={form.image_url} onChange={(url) => set("image_url", url)} />

              {collections.length > 0 && (
                <div>
                  <Label>Collections</Label>
                  <div className="flex flex-wrap gap-2">
                    {collections.map((c) => {
                      const checked = form.collection_ids.includes(c.id);
                      return (
                        <label
                          key={c.id}
                          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${
                            checked
                              ? "border-rose-500/50 bg-rose-500/15 text-rose-200"
                              : "border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:text-zinc-200"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={checked}
                            onChange={(e) =>
                              set("collection_ids", e.target.checked
                                ? [...form.collection_ids, c.id]
                                : form.collection_ids.filter((x) => x !== c.id))}
                          />
                          {c.name}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="p-badge">Badge</Label>
                  <Input id="p-badge" value={form.badge} onChange={(e) => set("badge", e.target.value)} placeholder="Best seller" />
                </div>
                <div>
                  <Label htmlFor="p-sort">Sort order</Label>
                  <Input id="p-sort" type="number" value={String(form.sort_order)} onChange={(e) => set("sort_order", Number(e.target.value))} />
                </div>
              </div>

              <div className="flex flex-wrap gap-4 pt-1">
                <label className="flex items-center gap-2 text-sm text-zinc-300">
                  <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} className="accent-rose-500" />
                  {form.published ? <Eye className="h-4 w-4 text-emerald-400" /> : <EyeOff className="h-4 w-4 text-zinc-500" />}
                  Published (live on the store)
                </label>
                <label className="flex items-center gap-2 text-sm text-zinc-300">
                  <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-rose-500" />
                  <Star className="h-4 w-4 text-amber-400" />
                  Featured on the home page
                </label>
              </div>
            </div>
          )}

          {tab === "pricing" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="p-type">Product type</Label>
                  <Select id="p-type" value={form.product_type} onChange={(e) => {
                    const t = e.target.value as typeof form.product_type;
                    set("product_type", t);
                    // Sensible fulfilment default per type.
                    if (t === "physical") set("fulfillment", "shipped");
                    else if (form.fulfillment === "shipped") set("fulfillment", "instant");
                  }}>
                    {Object.entries(PRODUCT_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="p-ful">After payment</Label>
                  <Select id="p-ful" value={form.fulfillment} onChange={(e) => set("fulfillment", e.target.value as typeof form.fulfillment)}>
                    {Object.entries(FULFILLMENT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </Select>
                </div>
              </div>
              <FieldHint>
                {form.fulfillment === "contact"
                  ? "Shoppers send an enquiry instead of paying — you follow up and close the sale yourself."
                  : form.fulfillment === "shipped"
                  ? "Paid orders appear under Orders → fulfil them with a tracking number."
                  : "Digital content (from the Digital content tab) unlocks in the customer's account the moment they pay."}
              </FieldHint>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="p-pt">Pricing</Label>
                  <Select id="p-pt" value={form.pricing_type} onChange={(e) => set("pricing_type", e.target.value as typeof form.pricing_type)}>
                    <option value="one_time">One-time</option>
                    <option value="subscription">Subscription</option>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="p-amt">Price ({form.currency})</Label>
                  <Input id="p-amt" value={form.amount} onChange={(e) => set("amount", e.target.value)} inputMode="decimal" />
                </div>
                <div>
                  <Label htmlFor="p-cmp">Compare-at</Label>
                  <Input id="p-cmp" value={form.compare_at} onChange={(e) => set("compare_at", e.target.value)} inputMode="decimal" placeholder="optional" />
                </div>
              </div>
              {form.pricing_type === "subscription" && (
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-200">
                  <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                  Subscriptions check out on their own (they can't be combined with other items in a cart)
                  and need Stripe connected.
                </div>
              )}
              {form.pricing_type === "subscription" && (
                <div className="w-40">
                  <Label htmlFor="p-int">Bills every</Label>
                  <Select id="p-int" value={form.recurring_interval} onChange={(e) => set("recurring_interval", e.target.value)}>
                    {["day", "week", "month", "year"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </Select>
                </div>
              )}

              <div className="border-t border-zinc-800 pt-4">
                <p className="mb-3 text-sm font-medium text-zinc-200">Inventory</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="p-sku">SKU</Label>
                    <Input id="p-sku" value={form.sku} onChange={(e) => set("sku", e.target.value)} placeholder="optional" />
                  </div>
                  <div>
                    <Label htmlFor="p-weight">Weight (grams)</Label>
                    <Input id="p-weight" value={form.weight_grams} onChange={(e) => set("weight_grams", e.target.value)} inputMode="numeric" placeholder="optional" />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-zinc-300">
                    <input type="checkbox" checked={form.track_inventory} onChange={(e) => set("track_inventory", e.target.checked)} className="accent-rose-500" />
                    Track inventory
                  </label>
                  {form.track_inventory && (
                    <label className="flex items-center gap-2 text-sm text-zinc-300">
                      <input type="checkbox" checked={form.allow_backorder} onChange={(e) => set("allow_backorder", e.target.checked)} className="accent-rose-500" />
                      Allow selling when out of stock
                    </label>
                  )}
                </div>
                {form.track_inventory && !(product?.has_variants) && (
                  <div className="mt-3 w-40">
                    <Label htmlFor="p-qty">Quantity in stock</Label>
                    <Input id="p-qty" type="number" min={0} value={String(form.inventory_quantity)} onChange={(e) => set("inventory_quantity", Number(e.target.value))} />
                  </div>
                )}
                {form.track_inventory && product?.has_variants && (
                  <FieldHint>This product has variants — set stock per variant on the Variants tab.</FieldHint>
                )}
              </div>

              <div className="border-t border-zinc-800 pt-4">
                <Label htmlFor="p-pay">External payment link (optional)</Label>
                <Input id="p-pay" value={form.payment_link} onChange={(e) => set("payment_link", e.target.value)} placeholder="https://your-hosted-checkout…" />
                <FieldHint>
                  For processors without an API: paste a hosted checkout/payment link. Buyers are sent
                  there to pay (single-item purchases only) and you mark the order paid under Orders.
                </FieldHint>
              </div>
            </div>
          )}

          {tab === "variants" && id && (
            <VariantsEditor
              productId={id}
              currency={form.currency}
              baseAmount={strToCents(form.amount)}
              initialOptions={product?.options ?? []}
              initialVariants={product?.variants ?? []}
              trackInventory={form.track_inventory}
              onSaved={onSaved}
            />
          )}

          {tab === "content" && id && <ResourcesManager productId={id} />}
        </div>

        <div className="border-t border-zinc-800 px-5 py-3">
          <ModalFooter>
            {id && <Button variant="ghost" className="text-rose-400 hover:text-rose-300" onClick={remove} leftIcon={<Trash2 className="h-4 w-4" />}>Archive</Button>}
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button onClick={save} loading={saving}>{isNew && !id ? "Create" : "Save"}</Button>
          </ModalFooter>
        </div>
      </div>
    </Drawer>
  );
}

// ─── Variants editor ────────────────────────────────────────────────────────
interface VariantDraft {
  id?: string;
  title: string;
  option_values: Record<string, string>;
  sku: string;
  amount: string;       // "" → inherit base price
  inventory_quantity: number;
}

function cartesian(options: { name: string; values: string[] }[]): Record<string, string>[] {
  if (!options.length) return [];
  return options.reduce<Record<string, string>[]>(
    (acc, opt) => acc.flatMap((combo) => opt.values.map((v) => ({ ...combo, [opt.name]: v }))),
    [{}],
  );
}

function VariantsEditor({
  productId, currency, baseAmount, initialOptions, initialVariants, trackInventory, onSaved,
}: {
  productId: string;
  currency: string;
  baseAmount: number;
  initialOptions: { name: string; values: string[] }[];
  initialVariants: StoreVariant[];
  trackInventory: boolean;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState<{ name: string; values: string }[]>(
    initialOptions.length
      ? initialOptions.map((o) => ({ name: o.name, values: o.values.join(", ") }))
      : [],
  );
  const [drafts, setDrafts] = useState<VariantDraft[]>(
    initialVariants.map((v) => ({
      id: v.id,
      title: v.title,
      option_values: v.option_values,
      sku: v.sku ?? "",
      amount: v.amount_cents != null ? centsToStr(v.amount_cents) : "",
      inventory_quantity: v.inventory_quantity,
    })),
  );

  const parsedOptions = useMemo(
    () => options
      .map((o) => ({ name: o.name.trim(), values: o.values.split(",").map((s) => s.trim()).filter(Boolean) }))
      .filter((o) => o.name && o.values.length),
    [options],
  );

  const regenerate = () => {
    const combos = cartesian(parsedOptions);
    setDrafts(combos.map((combo) => {
      const title = Object.values(combo).join(" / ");
      const existing = drafts.find((d) => d.title === title);
      return existing ?? { title, option_values: combo, sku: "", amount: "", inventory_quantity: 0 };
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await storeApi.saveVariants(productId, {
        options: parsedOptions,
        variants: drafts.map((d, i) => ({
          id: d.id,
          title: d.title,
          option_values: d.option_values,
          sku: d.sku || null,
          amount_cents: d.amount ? strToCents(d.amount) : null,
          inventory_quantity: Number(d.inventory_quantity) || 0,
          position: i * 10,
        })),
      });
      toast.success("Variants saved");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save variants");
    } finally {
      setSaving(false);
    }
  };

  const setDraft = (i: number, patch: Partial<VariantDraft>) =>
    setDrafts((ds) => ds.map((d, n) => (n === i ? { ...d, ...patch } : d)));

  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-400">
        Options (like Size or Colour) generate the variant grid. Leave a variant's price blank to
        use the product's base price ({formatMoney(baseAmount, currency)}).
      </p>

      <div className="space-y-2">
        {options.map((o, i) => (
          <div key={i} className="flex items-end gap-2">
            <div className="w-36">
              <Label>Option name</Label>
              <Input value={o.name} onChange={(e) => setOptions((os) => os.map((x, n) => n === i ? { ...x, name: e.target.value } : x))} placeholder="Size" />
            </div>
            <div className="flex-1">
              <Label>Values (comma-separated)</Label>
              <Input value={o.values} onChange={(e) => setOptions((os) => os.map((x, n) => n === i ? { ...x, values: e.target.value } : x))} placeholder="S, M, L, XL" />
            </div>
            <Button variant="ghost" size="sm" className="text-rose-400" onClick={() => setOptions((os) => os.filter((_, n) => n !== i))}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <div className="flex gap-2">
          {options.length < 3 && (
            <Button size="sm" variant="secondary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setOptions((os) => [...os, { name: "", values: "" }])}>
              Add option
            </Button>
          )}
          {parsedOptions.length > 0 && (
            <Button size="sm" variant="secondary" onClick={regenerate}>Generate variants</Button>
          )}
        </div>
      </div>

      {drafts.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-3 py-2 font-medium">Variant</th>
                <th className="px-3 py-2 font-medium">Price ({currency})</th>
                <th className="px-3 py-2 font-medium">SKU</th>
                {trackInventory && <th className="px-3 py-2 font-medium">Stock</th>}
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/70">
              {drafts.map((d, i) => (
                <tr key={d.title} className="bg-zinc-950">
                  <td className="px-3 py-2 font-medium text-zinc-200">{d.title}</td>
                  <td className="px-3 py-2">
                    <Input value={d.amount} onChange={(e) => setDraft(i, { amount: e.target.value })} placeholder={centsToStr(baseAmount)} className="w-24" inputMode="decimal" />
                  </td>
                  <td className="px-3 py-2">
                    <Input value={d.sku} onChange={(e) => setDraft(i, { sku: e.target.value })} placeholder="—" className="w-28" />
                  </td>
                  {trackInventory && (
                    <td className="px-3 py-2">
                      <Input type="number" min={0} value={String(d.inventory_quantity)} onChange={(e) => setDraft(i, { inventory_quantity: Number(e.target.value) })} className="w-20" />
                    </td>
                  )}
                  <td className="px-2 py-2 text-right">
                    <button onClick={() => setDrafts((ds) => ds.filter((_, n) => n !== i))} className="rounded p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-rose-300">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end gap-2">
        {drafts.length > 0 || initialVariants.length > 0 ? (
          <Button onClick={save} loading={saving}>Save variants</Button>
        ) : (
          <p className="text-xs text-zinc-500">No variants — this product sells as a single item.</p>
        )}
      </div>
    </div>
  );
}

// ─── Digital content (resources) manager ───────────────────────────────────
function ResourcesManager({ productId }: { productId: string }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [resources, setResources] = useState<StoreResource[] | null>(null);
  const [editing, setEditing] = useState<StoreResource | "new" | null>(null);

  const load = useCallback(() => {
    storeApi.resources(productId).then((r) => setResources(r.resources)).catch(() => setResources([]));
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const del = async (r: StoreResource) => {
    if (!(await confirm({ title: "Delete this content?", description: r.title, confirmLabel: "Delete", danger: true }))) return;
    try { await storeApi.deleteResource(r.id); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : "Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-400">
          Unlocked in the customer's account when they buy this product (instant-access products).
        </p>
        <Button size="sm" variant="secondary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>Add content</Button>
      </div>
      {resources === null ? (
        <Skeleton className="h-24" />
      ) : resources.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-800 p-6 text-center text-sm text-zinc-500">
          Nothing yet. Add the downloads, links, videos or welcome notes buyers should receive.
        </div>
      ) : (
        <div className="space-y-2">
          {resources.map((r) => {
            const Icon = RESOURCE_ICONS[r.resource_type] ?? Link2;
            return (
              <div key={r.id} className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
                <GripVertical className="mt-0.5 h-4 w-4 shrink-0 text-zinc-700" />
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-rose-300" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-zinc-100">{r.title}</div>
                  {r.description && <div className="truncate text-xs text-zinc-500">{r.description}</div>}
                  {r.url && (
                    <a href={r.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="mt-0.5 inline-flex items-center gap-1 text-xs text-rose-400 hover:underline">
                      {r.url} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {r.resource_type === "text" && r.body && <div className="mt-0.5 line-clamp-2 text-xs text-zinc-500">{r.body}</div>}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button onClick={() => setEditing(r)} className="rounded p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(r)} className="rounded p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {editing !== null && (
        <ResourceEditor
          productId={productId}
          resource={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ResourceEditor({
  productId, resource, onClose, onSaved,
}: {
  productId: string;
  resource: StoreResource | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: resource?.title ?? "",
    description: resource?.description ?? "",
    resource_type: resource?.resource_type ?? "link",
    url: resource?.url ?? "",
    body: resource?.body ?? "",
    sort_order: resource?.sort_order ?? 0,
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const isText = form.resource_type === "text";

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    const body = {
      title: form.title.trim(),
      description: form.description || null,
      resource_type: form.resource_type as StoreResource["resource_type"],
      url: isText ? null : form.url || null,
      body: isText ? form.body || null : null,
      sort_order: Number(form.sort_order) || 0,
    };
    try {
      if (resource) await storeApi.updateResource(resource.id, body);
      else await storeApi.createResource(productId, body);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={resource ? "Edit content" : "Add content"} size="md">
      <div className="space-y-4 p-5">
        <div>
          <Label htmlFor="r-title">Title</Label>
          <Input id="r-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Your download" />
        </div>
        <div>
          <Label htmlFor="r-type">Type</Label>
          <Select id="r-type" value={form.resource_type} onChange={(e) => set("resource_type", e.target.value as typeof form.resource_type)}>
            {["link", "file", "video", "download", "embed", "text"].map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </div>
        {isText ? (
          <div>
            <Label htmlFor="r-body">Content</Label>
            <Textarea id="r-body" rows={5} value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="Text shown inline in the customer's account." />
          </div>
        ) : (
          <div>
            <Label htmlFor="r-url">URL</Label>
            <Input id="r-url" value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://…" />
          </div>
        )}
        <div>
          <Label htmlFor="r-desc">Description</Label>
          <Input id="r-desc" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="optional" />
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving}>Save</Button>
      </ModalFooter>
    </Modal>
  );
}
